// ProvenanceFooter — the per-card provenance footer.
//
// REPLACES the inline `[[tel:]]` chips (do not render those). Each row maps a
// figure -> signal_key (store-mapped, GUID-guarded) + read_source + window +
// count + source-grain label (rollup_cagg => "as of last completed bucket").
import React from "react";

import type { FooterEntry } from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";

export interface ProvenanceFooterProps {
  entries: FooterEntry[];
}

const windowText = (e: FooterEntry): string => {
  if (e.window) return e.window;
  if (e.windowStart && e.windowEnd) return `${e.windowStart} → ${e.windowEnd}`;
  return "";
};

export const ProvenanceFooter: React.FC<ProvenanceFooterProps> = ({ entries }) => {
  const { translate } = useTranslation("machineEvidence");
  if (!entries || entries.length === 0) return null;

  // The grain label is CLIENT chrome keyed on the closed `source_grain` enum; an
  // unknown grain falls back to the engine's verbatim label (never a false claim).
  const grainText = (e: FooterEntry): string =>
    e.sourceGrain
      ? (translate(`grain.${e.sourceGrain}`, { defaultValue: e.grainLabel }) as string)
      : e.grainLabel;

  return (
    <div
      className="msc-provenance"
      style={{
        borderTop: `1px solid ${dt.rowBorder}`,
        paddingTop: 8,
        marginTop: 8,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        fontFamily: dt.fontMono,
        fontSize: 11,
        color: dt.textMuted,
      }}
    >
      {entries.map((e) => (
        <div
          key={`${e.figureId}-${e.signalKey}`}
          className="msc-provenance-row"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" }}
        >
          {/* signalKey is GUID-guarded upstream; blank when a leak was stripped. */}
          {e.signalKey ? (
            <span style={{ color: dt.textSecondary, fontWeight: 600 }}>{e.signalKey}</span>
          ) : null}
          {e.readSource ? <span>· {e.readSource}</span> : null}
          {windowText(e) ? <span>· {windowText(e)}</span> : null}
          {typeof e.count === "number" ? <span>· n={e.count.toLocaleString()}</span> : null}
          {e.grainLabel ? <span>· {grainText(e)}</span> : null}
        </div>
      ))}
    </div>
  );
};

export default ProvenanceFooter;
